/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators, Dispatch } from "redux";

import { PrimaryButton, SearchBox, Stack } from "@fluentui/react";

import ListCounts from "Components/Common/ListCounts/ListCounts";
import { Title } from "Components/Common/Results/Result.styles";
import M365CopilotBenefitList from "Components/M365CopilotBenefitList/M365CopilotBenefitList";
import {
  defaultFilterValues,
  defaultListingPageSize,
  m365CopilotFilterFields,
} from "Constants/M365CopilotBenefitConstants";
import { SubSiteUrlRootPath } from "Constants/TenantTypes";
import {
  buttonStackTokens,
  containerStackStyles,
  filterButtonStyles,
  listStackStyles,
  searchBoxStyles,
  tableStackStyles,
  PaddedFlexRow,
} from "Containers/Common/CommonContainer.styles";
import SideBarContainer from "Containers/SideBarContainer/SideBarContainer";
import {
  addM365CopilotFilterValue,
  removeM365CopilotFilterValue,
  resetM365CopilotBenefit,
  searchM365CopilotBenefit,
  updateM365CopilotCurrentPage,
  updateM365CopilotFilterValue,
  updateM365CopilotPageSize,
  M365CopilotBenefitActionTypes,
} from "Dataflows/M365CopilotBenefit/M365CopilotBenefitAction";
import {
  getM365CopilotBenefits,
  getM365CopilotBenefitsCurrentPage,
  getM365CopilotBenefitsCurrentTenant,
  getM365CopilotBenefitsFilteredCount,
  getM365CopilotBenefitsFilterValues,
  getM365CopilotBenefitsIsLoading,
  getM365CopilotBenefitsLoadError,
  getM365CopilotBenefitsPageSize,
  getM365CopilotBenefitsSearchKey,
} from "Dataflows/M365CopilotBenefit/M365CopilotBenefitSelector";
import { AppState } from "Dataflows/Reducers";
import { IFilterValue } from "Interfaces/IFilterValue";

import {
  IM365CopilotBenefitContainerProps,
  IM365CopilotBenefitContainerRouterParams,
} from "./IM365CopilotBenefitContainerProps";

/**
 * M365CopilotBenefitContainer. Display M365 Copilot Benefits and filter them.
 * @param props - the props for the component
 * @returns the element
 */
const M365CopilotBenefitContainer: React.FunctionComponent<IM365CopilotBenefitContainerProps> = (
  props: IM365CopilotBenefitContainerProps
): ReactElement => {
  const {
    benefits,
    isLoading,
    loadM365CopilotBenefitsError,
    searchText,
    filteredCount,
    pageSize,
    currentPage,
    m365CopilotFilterValues,
    currentTenant,
    actions: {
      loadBenefits,
      updateM365CopilotCurrentPage,
      updateM365CopilotPageSize,
      addM365CopilotFilterValue,
      updateM365CopilotFilterValue,
      removeM365CopilotFilterValue,
      resetM365CopilotBenefits,
    },
  } = props;

  const routeParams = useParams<IM365CopilotBenefitContainerRouterParams>();
  const tenant = SubSiteUrlRootPath[routeParams.tenant];

  const [searchTextState, setSearchTextState] = useState(searchText);

  const handleSearch = (
    searchText: string,
    selectedBenefitsFilterValues: IFilterValue[],
    searchPage: number,
    pageSize: number
  ): void => {
    loadBenefits(tenant, searchText, selectedBenefitsFilterValues, searchPage, pageSize);
  };

  const onSearchTextClear = (): void => {
    setSearchTextState("");
    handleSearch("", m365CopilotFilterValues, 1, pageSize);
  };

  const onReset = (): void => {
    resetM365CopilotBenefits();
    setSearchTextState("");
    handleSearch("", defaultFilterValues, 1, defaultListingPageSize);
  };

  useEffect(() => {
    if (isLoading) return;

    if (tenant !== currentTenant) {
      onReset();
      return;
    }

    handleSearch(searchText, m365CopilotFilterValues, currentPage, pageSize);
  }, [currentPage, pageSize, tenant]);

  return (
    <Stack horizontal styles={containerStackStyles}>
      <Stack.Item disableShrink>
        <SideBarContainer
          addFilterValue={addM365CopilotFilterValue}
          removeFilterValue={removeM365CopilotFilterValue}
          updateFilterValue={updateM365CopilotFilterValue}
          selectedFilterValues={m365CopilotFilterValues}
          filterFields={m365CopilotFilterFields}
        />
        <Stack horizontal tokens={buttonStackTokens}>
          <PrimaryButton
            styles={filterButtonStyles()}
            text="Apply"
            onClick={(): void =>
              handleSearch(searchText, m365CopilotFilterValues, currentPage, pageSize)
            }
          />
          <PrimaryButton styles={filterButtonStyles()} text="Reset" onClick={onReset} />
        </Stack>
      </Stack.Item>
      <Stack.Item grow styles={listStackStyles()}>
        <Stack styles={tableStackStyles}>
          <PaddedFlexRow>
            <Title>Benefits - M365 Copilot</Title>
            <ListCounts total={filteredCount} loading={isLoading} />
          </PaddedFlexRow>
          <SearchBox
            placeholder="Search benefits"
            value={searchTextState}
            onSearch={(searchValue: string): void =>
              handleSearch(searchValue, m365CopilotFilterValues, 1, pageSize)
            }
            onChange={(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string): void =>
              setSearchTextState(newValue || "")
            }
            onClear={onSearchTextClear}
            styles={searchBoxStyles}
          />
          <M365CopilotBenefitList
            data={benefits}
            filteredCount={filteredCount}
            pageSize={pageSize}
            isLoadingM365CopilotBenefit={isLoading}
            currentPage={currentPage}
            loadM365CopilotBenefitError={loadM365CopilotBenefitsError}
            tenant={tenant}
            actions={{
              updateCurrentPage: updateM365CopilotCurrentPage,
              updatePageSize: updateM365CopilotPageSize,
            }}
          />
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

const mapStateToProps = (state: AppState) => ({
  benefits: getM365CopilotBenefits(state.m365CopilotBenefitState),
  isLoading: getM365CopilotBenefitsIsLoading(state.m365CopilotBenefitState),
  loadM365CopilotBenefitsError: getM365CopilotBenefitsLoadError(state.m365CopilotBenefitState),
  searchText: getM365CopilotBenefitsSearchKey(state.m365CopilotBenefitState),
  filteredCount: getM365CopilotBenefitsFilteredCount(state.m365CopilotBenefitState),
  pageSize: getM365CopilotBenefitsPageSize(state.m365CopilotBenefitState),
  currentPage: getM365CopilotBenefitsCurrentPage(state.m365CopilotBenefitState),
  m365CopilotFilterValues: getM365CopilotBenefitsFilterValues(state.m365CopilotBenefitState),
  currentTenant: getM365CopilotBenefitsCurrentTenant(state.m365CopilotBenefitState),
});

const mapDispatchToProps = (dispatch: Dispatch<M365CopilotBenefitActionTypes>) => ({
  actions: {
    loadBenefits: bindActionCreators(searchM365CopilotBenefit, dispatch),
    updateM365CopilotCurrentPage: bindActionCreators(updateM365CopilotCurrentPage, dispatch),
    updateM365CopilotPageSize: bindActionCreators(updateM365CopilotPageSize, dispatch),
    addM365CopilotFilterValue: bindActionCreators(addM365CopilotFilterValue, dispatch),
    updateM365CopilotFilterValue: bindActionCreators(updateM365CopilotFilterValue, dispatch),
    removeM365CopilotFilterValue: bindActionCreators(removeM365CopilotFilterValue, dispatch),
    resetM365CopilotBenefits: bindActionCreators(resetM365CopilotBenefit, dispatch),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(M365CopilotBenefitContainer);
